import CloseIcon from '@mui/icons-material/Close';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Backdrop, Box, IconButton, Paper, styled } from '@mui/material';
import React from 'react';
import { gradients } from '../ColorPaper';
import { MenuIconButton } from '../MenuIconButton';
const defaultWidth = 400;
const TopBarContent = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
});
/**
 * @deprecated Replaced by `ModalSlideout` due to accessibility issues.
 *
 * Renders a slide-out side panel that appears from the right side of the screen.
 * Includes a top bar with optional close and menu buttons, customizable width, gradient overlay,
 * and scroll detection. This component should no longer be used in favor of `ModalSlideout`.
 *
 * @param {SlideoutProps} props - Props for configuring the slideout panel.
 * @param {boolean} props.open - Whether the slideout is open and visible.
 * @param {number|string} [props.width] - Optional custom width for the slideout.
 * @param {boolean} [props.fullContent] - Whether the slideout fills its container.
 * @param {boolean} [props.darkTopBar] - Applies dark styling to the top bar.
 * @param {keyof typeof gradients} [props.gradientTopBar] - Gradient key to use for the top bar background when scrolled.
 * @param {{ label: string; onClick: () => void; 'data-test'?: string }[]} [props.menuItems] - Optional action menu items.
 * @param {() => void} [props.onClose] - Function called when the user requests to close the slideout.
 * @param {string} [props.'data-test'] - Optional test ID for the slideout container.
 * @returns {JSX.Element} The rendered slideout panel component.
 */
export const Slideout = (props) => {
    var _a, _b;
    const { open } = props;
    const [backdrop, setBackdrop] = React.useState(false);
    const [isScrolledToTop, setIsScrolledToTop] = React.useState(true);
    const showBackdrop = React.useCallback(() => {
        setBackdrop(true);
    }, []);
    const hideBackdrop = React.useCallback(() => {
        setBackdrop(false);
    }, []);
    const { onClose } = props;
    const handleClose = React.useCallback(() => {
        if (onClose) {
            hideBackdrop(); // Need to hide backdrop explicitly because otherwise it does not hide on mobile/tablet
            onClose();
        }
    }, [hideBackdrop, onClose]);
    React.useEffect(() => {
        if (!open && backdrop) {
            hideBackdrop();
        }
    }, [open, backdrop, hideBackdrop]);
    const onScroll = (e) => {
        const fromTop = 56;
        if (e.currentTarget.scrollTop < fromTop && !isScrolledToTop) {
            setIsScrolledToTop(true);
        }
        else if (e.currentTarget.scrollTop >= fromTop && isScrolledToTop) {
            setIsScrolledToTop(false);
        }
    };
    const xsUpWidth = typeof props.width === 'string' ? `min(100vw, ${props.width})` : `min(100vw, ${(_a = props.width) !== null && _a !== void 0 ? _a : defaultWidth}px)`;
    return (React.createElement(React.Fragment, null,
        React.createElement(Backdrop, { sx: { zIndex: (theme) => theme.zIndex.drawer + 1 }, open: backdrop }),
        React.createElement(Paper, { sx: {
                display: 'flex',
                flexGrow: 1,
                position: 'fixed',
                top: 0,
                right: 0,
                bottom: 0,
                borderRadius: 0,
                overflow: 'hidden',
                zIndex: (theme) => theme.zIndex.drawer + 2,
                transition: (theme) => !open
                    ? theme.transitions.create('width') + ', ' + theme.transitions.create('box-shadow', { easing: 'step-end' })
                    : theme.transitions.create('width') + ', ' + theme.transitions.create('box-shadow', { easing: 'step-start' }),
                width: open
                    ? {
                        xs: '100%',
                        sm: xsUpWidth,
                    }
                    : 0,
                boxShadow: !open ? 'none' : '',
            }, elevation: 8, onMouseEnter: showBackdrop, onMouseLeave: hideBackdrop, "data-test": props['data-test'] },
            React.createElement(Box, { sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    overflow: 'auto',
                    position: 'relative',
                    width: {
                        xs: '100%',
                        sm: xsUpWidth,
                    },
                }, "data-test": 'slideout-content' },
                React.createElement(Box, { sx: {
                        position: props.fullContent ? 'absolute' : 'relative',
                        width: '100%',
                        zIndex: (theme) => theme.zIndex.drawer,
                    } },
                    React.createElement(TopBarContent, { sx: {
                            p: 1,
                            // theme.palette.action.active is default icon color
                            color: (theme) => (props.darkTopBar ? theme.palette.background.paper : theme.palette.action.active),
                            zIndex: (theme) => theme.zIndex.drawer,
                        } },
                        open && onClose && (React.createElement(IconButton, { onClick: handleClose, "data-test": 'slideout-close-button', "aria-label": 'Close Slideout', color: 'inherit' },
                            React.createElement(CloseIcon, null))),
                        open && !!((_b = props.menuItems) === null || _b === void 0 ? void 0 : _b.length) && (React.createElement(Box, { sx: {
                                ml: 'auto',
                            } },
                            React.createElement(MenuIconButton, { icon: React.createElement(MoreHorizIcon, null), menuItems: props.menuItems, "data-test": 'more-button', "aria-label": 'More' })))),
                    props.gradientTopBar && (React.createElement(Box, { sx: Object.assign(Object.assign({}, gradients[props.gradientTopBar]), { bottom: 0, left: 0, right: 0, top: 0, position: 'absolute', opacity: !isScrolledToTop ? 1 : 0, transition: (theme) => theme.transitions.create('opacity') }) }))),
                React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', flexGrow: 1, p: props.fullContent ? 0 : 2, overflow: 'auto' }, onScroll: onScroll }, props.children)))));
};
